import * as React from 'react';
import { useContext, useState } from 'react';
import { AppContext } from '../../context/AppContext';
import InlineMessage, { InlineMessageState } from '../InlineMessage';
import PasswordStrengthComponent from '../PasswordStrengthComponent';

/**
 * PasswordChangeForm
 *
 * PUT /user/password wants the current password plus the new one twice - the
 * repetition is checked on the server as well, but a round trip is a poor way to
 * tell someone they mistyped, so it is caught here first.
 *
 * the strength meter is the existing PasswordStrengthComponent: it fetches the
 * policy from the server through SystemService, so the rules shown here are the
 * same ones the backend enforces and cannot drift apart.
 */
const PasswordChangeForm: React.FC = () => {
  const {
    services: {
      serviceTwoFactor
    },
    I18N
  } = useContext(AppContext);

  const [ currentPassword, setCurrentPassword ] = useState('');
  const [ password, setPassword ] = useState('');
  const [ passwordConfirmation, setPasswordConfirmation ] = useState('');
  const [ message, setMessage ] = useState<InlineMessageState | null>(null);
  const [ busy, setBusy ] = useState(false);

  /**
   * mismatch
   * only complained about once there is something to compare - an empty second
   * field is not a mistake yet, it is an unfinished form
   */
  const mismatch = passwordConfirmation.length > 0 && password !== passwordConfirmation;

  /**
   * handleSubmit
   * @param event
   */
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (busy) {
      return;
    }

    if (password !== passwordConfirmation) {
      setMessage({
        kind: 'error',
        text: I18N.t("Die beiden neuen Passwörter stimmen nicht überein.")
      });

      return;
    }

    setMessage(null);
    setBusy(true);

    try {
      await serviceTwoFactor.changePassword(currentPassword, password, passwordConfirmation);

      /**
       * emptied on success so no password is left in the dom of a screen the
       * user may walk away from
       */
      setCurrentPassword('');
      setPassword('');
      setPasswordConfirmation('');
      setMessage({
        kind: 'success',
        text: I18N.t("Das Passwort wurde geändert.")
      });
    } catch (error) {
      const response = (error as any) && (error as any).response,
        code = response && response.status;

      let text = I18N.t("Das Passwort konnte nicht geändert werden.");

      if (code === 401 || code === 403) {
        text = I18N.t("Das aktuelle Passwort ist falsch.");
      } else if (code === 422) {
        text = I18N.t("Das neue Passwort erfüllt die Anforderungen nicht.");
      } else if (code === 429) {
        text = I18N.t("Zu viele Versuche. Bitte warten Sie einen Moment.");
      }

      setMessage({ kind: 'error', text: text });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-password-change>
      <InlineMessage section="password-change" message={ message } />

      <dia-form>
        <form onSubmit={ handleSubmit }>
          <dia-form-area>
            <dia-form-line>
              <dia-form-control>
                <input
                  type="password"
                  id="password-change-current"
                  name="password-change-current"
                  autoComplete="current-password"
                  placeholder={ I18N.t("Geben Sie Ihr aktuelles Passwort ein") }
                  value={ currentPassword }
                  onChange={ (event) => setCurrentPassword(event.target.value) }
                  required
                />
                <label htmlFor="password-change-current">{ I18N.t("Aktuelles Passwort") }</label>
              </dia-form-control>
            </dia-form-line>

            <dia-form-line>
              <dia-form-control>
                <input
                  type="password"
                  id="password-change-new"
                  name="password-change-new"
                  autoComplete="new-password"
                  aria-describedby="password-change-strength"
                  placeholder={ I18N.t("Geben Sie Ihr neues Passwort ein") }
                  value={ password }
                  onChange={ (event) => setPassword(event.target.value) }
                  required
                />
                <label htmlFor="password-change-new">{ I18N.t("Neues Passwort") }</label>
              </dia-form-control>
            </dia-form-line>

            <dia-form-line>
              <div data-password-change-strength id="password-change-strength">
                <PasswordStrengthComponent password={ password } />
              </div>
            </dia-form-line>

            <dia-form-line>
              <dia-form-control>
                <input
                  type="password"
                  id="password-change-repeat"
                  name="password-change-repeat"
                  autoComplete="new-password"
                  aria-invalid={ mismatch }
                  aria-describedby={ mismatch ? "password-change-repeat-error" : undefined }
                  placeholder={ I18N.t("Wiederholen Sie das neue Passwort") }
                  value={ passwordConfirmation }
                  onChange={ (event) => setPasswordConfirmation(event.target.value) }
                  required
                />
                <label htmlFor="password-change-repeat">{ I18N.t("Neues Passwort wiederholen") }</label>
              </dia-form-control>
            </dia-form-line>

            { /* the region is only about this one field, so it sits next to it
                 and not in the shared message block at the top */ }
            <dia-form-line>
              <p
                id="password-change-repeat-error"
                data-password-change-mismatch
                aria-live="polite"
              >
                { mismatch ? I18N.t("Die beiden neuen Passwörter stimmen nicht überein.") : "" }
              </p>
            </dia-form-line>

            <dia-form-line>
              <dia-form-control>
                <button
                  type="submit"
                  data-primary-dark
                  data-password-change-submit
                  disabled={ busy || mismatch }
                >
                  { I18N.t("Passwort ändern") }
                </button>
              </dia-form-control>
            </dia-form-line>
          </dia-form-area>
        </form>
      </dia-form>
    </div>
  );
};

export default PasswordChangeForm;
